/**
 * PLRChatView — Standalone chat-first quoting view.
 *
 * Replaces the ChatQuoteLoader + FieldInputCard + post-quote chat in the
 * InlineWorkflow panel. Single component, single state, single input bar.
 *
 * Usage in RateQuoteLinkInlineWorkflow.tsx:
 *   Replace the ChatQuoteLoader block + FieldInputCard block + footer with:
 *   <PLRChatView runId={backendId} insuredName="..." onSubmitField={submitStepInput} />
 *
 * States:
 *   connecting  → skeleton loader
 *   streaming   → live agent messages + tool cards
 *   awaiting    → input bar glows, user types answer
 *   complete    → suggestions chips, post-quote Q&A via same input bar
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiCheck, FiX, FiTool, FiMessageSquare, FiSend, FiRefreshCw } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { useChatStream, type ChatMessage } from './useChatStream';
import EmailDraftCard from './EmailDraftCard';

// ── Tool labels ─────────────────────────────────────────────────────────────
const TOOL_LABELS: Record<string, string> = {
  // Shared
  get_insured_id_tool: 'Looking up insured',
  check_personal_lines_appetite_tool: 'Checking carrier appetite',
  get_insured_details_tool: 'Fetching insured details',
  start_personal_lines_quote_tool: 'Creating application',
  get_personal_lines_quote_by_id_tool: 'Reading prefilled data',
  get_personal_lines_quote_results_tool: 'Submitting to carriers',
  save_quote_to_ams_tool: 'Saving to AMS',
  get_template_field_options_tool: 'Fetching field options',
  // PAP
  get_pap_template_tool: 'Fetching PAP template',
  get_vehicle_by_vin_tool: 'Decoding VIN',
  apply_pap_quote_template_tool: 'Applying quote data',
  // PAP — GATE 2 correction tools
  update_quote_coverages_tool: 'Fixing coverage values',
  update_quote_customer_info_tool: 'Updating contact info',
  update_quote_driver_details_tool: 'Correcting driver data',
  delete_vehicle_from_quote_tool: 'Removing excluded vehicle',
  get_dropdown_values_tool: 'Looking up dropdown values',
  fetch_pap_template_tool: 'Fetching state template',
  update_quote_policy_details_tool: 'Advancing policy step',
  // HO
  get_home_template_tool: 'Fetching HO template',
  fetch_home_template_tool: 'Fetching HO state template',
  get_home_dropdown_values_tool: 'Fetching dropdown values',
  apply_home_quote_template_tool: 'Applying quote data',
  get_home_quote_results_tool: 'Submitting to carriers',
  update_home_customer_info_tool: 'Updating insured contact',
  update_home_structure_details_tool: 'Fixing structure details',
  get_insured_id_home_tool: 'Looking up insured',
  check_appetite_home_tool: 'Checking HO carrier appetite',
  get_insured_details_home_tool: 'Fetching insured details',
  start_home_quote_tool: 'Creating HO application',
  get_home_quote_by_id_tool: 'Reading prefilled data',
  // Shared vehicle tools
  get_vehicle_makes_by_year_tool: 'Looking up vehicle makes',
  get_vehicle_models_tool: 'Looking up vehicle models',
  // Bundle tools
  get_insured_id_bundle_tool: 'Looking up insured',
  create_prospect_bundle_tool: 'Creating prospect',
  check_appetite_bundle_tool: 'Checking Home + Auto appetite',
  start_bundle_quote_tool: 'Starting Home leg',
  get_bundle_quote_by_id_tool: 'Reading application data',
  apply_bundle_home_quote_template_tool: 'Applying Home template',
  start_bundle_auto_leg_tool: 'Starting Auto leg',
  apply_bundle_pap_quote_template_tool: 'Applying Auto template',
  get_bundle_quote_results_tool: 'Retrieving combined results',
  // CLR — WC
  linqura_enrich: 'Enriching business data',
  get_cl_rating_data_from_ams: 'Fetching AMS CL rating data',
  get_insured_id_wc_tool: 'Looking up insured',
  get_naics_codes: 'Looking up NAICS code',
  get_naics_codes_tool: 'Looking up NAICS code',
  check_commercial_lines_appetite: 'Checking carrier appetite',
  check_commercial_lines_appetite_tool: 'Checking carrier appetite',
  start_wc_quote: 'Creating WC application',
  start_wc_quote_tool: 'Creating WC application',
  get_wc_quote_by_id_tool: 'Verifying application',
  get_wc_industry_codes: 'Fetching industry codes',
  get_wc_industry_codes_tool: 'Fetching industry codes',
  get_wc_class_codes: 'Selecting class codes',
  get_wc_class_codes_tool: 'Selecting class codes',
  update_wc_quote: 'Updating application',
  update_wc_quote_tool: 'Updating application',
  submit_wc_underwriting_tool: 'Answering underwriting questions',
  get_wc_quote_results: 'Retrieving quote results',
  get_wc_quote_results_tool: 'Retrieving quote results',
};
const HIDDEN_TOOLS = new Set([
  // PLR orchestration
  'plr_step_tracker', 'plr_finalize_run', 'plr_request_user_input', 'plr_get_vehicle_cost',
  // CLR orchestration
  'wc_step_tracker', 'wc_finalize_run', 'wc_request_user_input',
]);

// ── Props ───────────────────────────────────────────────────────────────────
interface Props {
  /** Backend run ID (UUID, not local). null = not connected yet. */
  runId: string | null;
  insuredName?: string;
  /** Run status from parent — 'completed'/'failed' skips SSE, loads saved events directly. */
  runStatus?: string;
  /** API base path — 'rate-quoting' (default) or 'commercial-rating' */
  basePath?: string;
  /** Called when user submits a field answer during awaiting_input. */
  onSubmitField?: (runId: string, stepId: string, values: Record<string, string>) => void;
  /** Called when user sends a post-quote chat message. */
  onSendChat?: (text: string) => void;
}

// ── Component ───────────────────────────────────────────────────────────────
const PLRChatView: React.FC<Props> = ({ runId, insuredName, runStatus, basePath, onSubmitField, onSendChat }) => {
  const isFinished = runStatus === 'completed' || runStatus === 'failed';
  const { events, status, thinking, sendMessage, addUserInput, loadSavedEvents } = useChatStream(runId, isFinished, basePath);
  const [input, setInput] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);
  const [waitingForAgent, setWaitingForAgent] = useState(false);
  const prevEventCountRef = useRef(events.length);

  // Derived state
  const isAwaiting = events.some(e => e.role === 'system' && e.input_request);
  const hasRunComplete = events.some(e => e.role === 'system' && e.content === 'run_complete');
  const isComplete = hasRunComplete || isFinished;
  const showInput = true;
  const lastAwait = [...events].reverse().find(e => e.role === 'system' && e.input_request) as any;

  // Clear waiting-for-agent only when an AGENT event arrives (not user_input duplicates)
  useEffect(() => {
    if (events.length > prevEventCountRef.current && waitingForAgent) {
      const newest = events[events.length - 1];
      if (newest && newest.role !== 'user') {
        setWaitingForAgent(false);
      }
    }
    prevEventCountRef.current = events.length;
  }, [events.length, waitingForAgent]);

  // Auto-scroll — use RAF to ensure DOM has updated
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, [events, thinking, waitingForAgent]);

  // SSE endpoint serves saved events for completed runs — no separate load needed

  // ── Submit handler ──────────────────────────────────────────────────────
  const handleSubmit = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    setInput('');

    if (isAwaiting && !isComplete && onSubmitField && runId && lastAwait) {
      // User answering a field prompt — show skeleton while agent processes
      addUserInput(text);
      setWaitingForAgent(true);
      const stepId = lastAwait.step_id || lastAwait.stepId || 's3'; // step_id always set by plr_request_user_input
      onSubmitField(runId, stepId, { user_response: text });
    } else {
      // Post-quote Q&A or any freeform chat — always allow
      if (onSendChat) onSendChat(text);
      else sendMessage(text);
    }
  }, [input, isAwaiting, isComplete, onSubmitField, onSendChat, runId, lastAwait, addUserInput, sendMessage]);

  // ── Render message ──────────────────────────────────────────────────────
  const renderMsg = (msg: ChatMessage, idx: number) => {
    switch (msg.role) {
      case 'assistant':
        return (
          <div key={idx} className="plr-chat__msg plr-chat__msg--ai">
            <span className="plr-chat__avatar"><RiSparklingFill size={11} /></span>
            <div className="plr-chat__bubble plr-chat__bubble--ai"
              dangerouslySetInnerHTML={{ __html: fmtMd(msg.content) }}
            />
          </div>
        );

      case 'tool': {
        // Only render tool-running cards; tool-done/error are visual updates to the running card
        if (msg.status !== 'running') return null;
        const toolName = msg.tool || '';
        if (HIDDEN_TOOLS.has(toolName)) return null;
        const isDone = events.slice(idx + 1).some(
          e => (e.role === 'tool' && e.status !== 'running') || e.role === 'assistant'
        );
        return (
          <div key={idx} className={`plr-chat__tool-card ${isDone ? 'plr-chat__tool-card--done' : ''}`}>
            <div className="plr-chat__tool-header">
              {isDone ? <FiCheck size={11} style={{ color: '#059669' }} /> : <FiTool size={11} />}
              <span className="plr-chat__tool-name">{TOOL_LABELS[toolName] || toolName.replace(/_/g, ' ')}</span>
              {isDone
                ? <span style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>Done</span>
                : <FiRefreshCw size={10} className="plr-chat__tool-spin" />
              }
            </div>
          </div>
        );
      }

      case 'user':
        return (
          <div key={idx} className="plr-chat__msg plr-chat__msg--user">
            <div className="plr-chat__bubble plr-chat__bubble--user">{msg.content}</div>
          </div>
        );

      case 'system':
        if (msg.input_request) {
          return (
            <React.Fragment key={idx}>
              <div className="plr-chat__msg plr-chat__msg--ai">
                <span className="plr-chat__avatar"><RiSparklingFill size={11} /></span>
                <div className="plr-chat__bubble plr-chat__bubble--ai plr-chat__bubble--await"
                  dangerouslySetInnerHTML={{ __html: '<span class="plr-chat__await-icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>' + fmtMd(msg.content || 'Your input is needed to continue.') }}
                />
              </div>
              {(msg as any).email_draft && (
                <div className="plr-chat__msg plr-chat__msg--ai">
                  <span className="plr-chat__avatar" style={{ visibility: 'hidden' }}><RiSparklingFill size={11} /></span>
                  <EmailDraftCard emailDraft={(msg as any).email_draft} />
                </div>
              )}
            </React.Fragment>
          );
        }
        if (msg.content === 'run_complete') {
          return (
            <div key={idx} className="plr-chat__msg plr-chat__msg--ai">
              <span className="plr-chat__avatar"><RiSparklingFill size={11} /></span>
              <div className="plr-chat__bubble plr-chat__bubble--ai plr-chat__bubble--done">
                <FiCheck size={14} />
                <span>Quote complete — {msg.quotes_count || 0} carrier(s) responded.</span>
              </div>
            </div>
          );
        }
        return null;

      default:
        return null;
    }
  };

  // ── Suggestions ─────────────────────────────────────────────────────────
  const SUGGESTIONS = ['Compare carriers', 'Explain best quote', 'Re-quote with higher BI', 'Add rental reimbursement'];

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="plr-cv">
      {/* Thread */}
      <div className="plr-cv__thread" ref={threadRef}>
        {/* Setup loader — shown while agent is being provisioned */}
        {events.length === 0 && status !== 'complete' && status !== 'error' && (
          <div className="plr-cv__setup">
            <div className="plr-cv__setup-icon">
              <RiSparklingFill size={20} />
            </div>
            <div className="plr-cv__setup-content">
              <div className="plr-cv__setup-title">Preparing your quote</div>
              <div className="plr-cv__setup-name">{insuredName || 'the insured'}</div>
              <div className="plr-cv__setup-steps">
                <div className="plr-cv__setup-step plr-cv__setup-step--active">
                  <FiRefreshCw size={10} className="plr-chat__tool-spin" />
                  <span>Connecting to quoting agent</span>
                </div>
                <div className="plr-cv__setup-step">
                  <span className="plr-cv__setup-dot" />
                  <span>Attaching carrier tools</span>
                </div>
                <div className="plr-cv__setup-step">
                  <span className="plr-cv__setup-dot" />
                  <span>Loading workflow skill</span>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Empty state — completed run with no saved chat events */}
        {events.length === 0 && status === 'complete' && (
          <div className="plr-cv__skel">
            <span className="plr-chat__avatar"><RiSparklingFill size={11} /></span>
            <p className="plr-cv__skel-text">No chat history available for this run.</p>
          </div>
        )}

        {events.map(renderMsg)}

        {/* Agent working indicator — shimmer while agent is processing */}
        {(thinking || waitingForAgent || (events.length > 0 && !isComplete && !isAwaiting && status === 'streaming')) && (
          <div className="plr-chat__msg plr-chat__msg--ai">
            <span className="plr-chat__avatar"><RiSparklingFill size={11} /></span>
            <div className="plr-chat__bubble plr-chat__bubble--ai plr-chat__bubble--skel">
              <div className="plr-cv__skel-line plr-cv__skel-line--sm" />
              <div className="plr-cv__skel-line plr-cv__skel-line--xs" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Suggestions */}
      {isComplete && (
        <div className="plr-cv__chips">
          {SUGGESTIONS.map(s => (
            <button key={s} className="plr-cv__chip" onClick={() => { setInput(''); sendMessage(s); }}>
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input bar */}
      {showInput && (
        <div className={`plr-cv__input ${isAwaiting && !isComplete ? 'plr-cv__input--active' : ''}`}>
          <FiMessageSquare size={14} className="plr-cv__input-icon" />
          <input
            className="plr-cv__input-field"
            placeholder={isAwaiting && !isComplete ? 'Type your answer and press Enter...' : 'Ask about results, compare carriers...'}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
            disabled={thinking || waitingForAgent}
            autoFocus={isAwaiting && !isComplete}
          />
          <button className="plr-cv__send" onClick={handleSubmit} disabled={thinking || waitingForAgent || !input.trim()}>
            <FiSend size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

// ── Markdown ────────────────────────────────────────────────────────────────

/** Parse a markdown table block into HTML table. */
function parseTable(block: string): string {
  const rows = block.trim().split('\n').filter(r => r.trim());
  if (rows.length < 2) return block;

  // Header row
  const headerCells = rows[0].split('|').map(c => c.trim()).filter(Boolean);
  // Separator row (|---|---|) — skip, but detect alignment
  const sepRow = rows[1];
  const alignments = sepRow.split('|').map(c => c.trim()).filter(Boolean).map(c => {
    if (c.startsWith(':') && c.endsWith(':')) return 'center';
    if (c.endsWith(':')) return 'right';
    return 'left';
  });
  // Data rows
  const dataRows = rows.slice(2);

  // Detect which rows are quoted (have a premium) to find the best quote
  const isQuoteTable = headerCells.some(h => /carrier|premium|status/i.test(h));
  let bestRowIdx = -1;
  if (isQuoteTable) {
    // Find first row with QUOTED status — that's the best (agent sorts by price)
    dataRows.forEach((row, ri) => {
      if (bestRowIdx >= 0) return;
      const cells = row.split('|').map(c => c.trim()).filter(Boolean);
      if (cells.some(c => /quoted/i.test(c)) && cells.some(c => /\$[\d,]+/.test(c))) {
        bestRowIdx = ri;
      }
    });
  }

  let html = '<div class="plr-table-wrap"><table class="plr-table">';
  html += '<thead><tr>';
  headerCells.forEach((cell, i) => {
    const align = alignments[i] || 'left';
    html += `<th style="text-align:${align}">${inlineMd(cell)}</th>`;
  });
  html += '</tr></thead><tbody>';
  dataRows.forEach((row, rowIdx) => {
    const cells = row.split('|').map(c => c.trim()).filter(Boolean);
    const isBest = rowIdx === bestRowIdx;
    html += `<tr${isBest ? ' class="plr-tr--best"' : ''}>`;
    cells.forEach((cell, i) => {
      const align = alignments[i] || 'left';
      // Status cells → badges; Carrier names → bold
      let cls = '';
      const lower = cell.toLowerCase().trim();
      let rendered = inlineMd(cell);

      // Status badge detection
      const isStatusCol = /^[✓✗xX]?\s*(quoted|failed|declined|pending|referral|success)/i.test(lower);
      if (isStatusCol) {
        if (lower.includes('fail')) {
          cls = ' class="plr-td--status"';
          rendered = `<span class="plr-status plr-status--fail">Failed</span>`;
        } else if (lower.includes('decline')) {
          cls = ' class="plr-td--status"';
          rendered = `<span class="plr-status plr-status--decline">Declined</span>`;
        } else if (lower.includes('quoted') && lower.includes('bridge')) {
          cls = ' class="plr-td--status"';
          rendered = `<span class="plr-status plr-status--bridge">Bridge</span>`;
        } else if (lower.includes('quoted') && lower.includes('referral')) {
          cls = ' class="plr-td--status"';
          rendered = `<span class="plr-status plr-status--referral">Referral</span>`;
        } else if (lower.includes('quoted')) {
          cls = ' class="plr-td--status"';
          rendered = `<span class="plr-status plr-status--quoted">Quoted</span>`;
        } else if (lower.includes('pending')) {
          cls = ' class="plr-td--status"';
          rendered = `<span class="plr-status plr-status--pending">Pending</span>`;
        }
      }
      // First column (carrier name) — make bold
      else if (i === 0 && isQuoteTable) {
        rendered = `<strong>${rendered}</strong>`;
      }
      // Last cell of best row — append the AI Pick bookmark
      const bookmark = (isBest && i === cells.length - 1)
        ? ' <span class="plr-aipick">★ AI Pick</span>' : '';
      html += `<td${cls} style="text-align:${align}">${rendered}${bookmark}</td>`;
    });
    html += '</tr>';
  });
  html += '</tbody></table></div>';
  return html;
}

/** Inline markdown only (bold, italic, code, links, currency, badges) — no blocks. */
function inlineMd(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code class="plr-code">$1</code>')
    // Markdown links
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_m, text, url) => {
      const isBind = /\b(bind|bridge)\b/i.test(text) || /\b(bind|offer|quote|apply|enroll|portal)\b/i.test(url);
      if (isBind) {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="plr-bind-btn plr-bind-btn--sm">` +
          `Bind <span class="plr-bind-btn__icon">🔗</span></a>`;
      }
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="plr-link">${text}</a>`;
    })
    // Bare URLs
    .replace(/(?<!href=")(https?:\/\/[^\s<)"]+)/g, (url) => {
      const isBind = /\b(bind|offer|quote|apply|enroll|portal)\b/i.test(url);
      if (isBind) {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="plr-bind-btn plr-bind-btn--sm">` +
          `Bind <span class="plr-bind-btn__icon">🔗</span></a>`;
      }
      const label = url.length > 40 ? url.slice(0, 37) + '...' : url;
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="plr-link">${label}</a>`;
    })
    // Currency amounts → green bold
    .replace(/\$[\d,]+(?:\.\d{1,2})?(?:\/\w+)?/g, (m) => `<span class="plr-price">${m}</span>`)
    // "Best Rate" / "Best Price" / "Best & Only" badges
    .replace(/(←\s*)?(Best\s*(Rate|Price|Quote|&amp;\s*Only\s*Quote?)\s*(→|←)?)/gi,
      (_m, pre, text) => `${pre || ''}<span class="plr-badge-best">★ ${text.replace(/←|→/g, '').trim()}</span>`);
}

function fmtMd(t: string): string {
  if (!t) return '';
  // Escape HTML
  let s = t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Extract and convert markdown tables before other transforms
  // Table = 2+ lines starting with |, second line is separator (|---|)
  s = s.replace(
    /((?:^[ \t]*\|.+\|[ \t]*$\n?){2,})/gm,
    (match) => {
      // Verify second line is a separator row
      const lines = match.trim().split('\n');
      if (lines.length >= 2 && /^\|[\s:|-]+\|$/.test(lines[1].trim())) {
        return parseTable(match);
      }
      return match;
    }
  );

  return s
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Inline code — styled nicely
    .replace(/`(.+?)`/g, '<code class="plr-code">$1</code>')
    // Headers → styled divs
    .replace(/^#{1,2}\s+(.+)$/gm, '<div class="plr-heading">$1</div>')
    .replace(/^#{3}\s+(.+)$/gm, '<div class="plr-subheading">$1</div>')
    // Bullets
    .replace(/^[-•]\s+(.+)$/gm, '<div class="plr-bullet">$1</div>')
    .replace(/^\d+\.\s+(.+)$/gm, '<div class="plr-bullet plr-bullet--num">$1</div>')
    // Markdown links [text](url) → styled links / bind buttons / PDF buttons
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_m, text, url) => {
      const lower = text.toLowerCase();
      const isBind = /\b(bind|bridge)\b/i.test(lower) || /\b(bind|offer|quote|apply|enroll|portal)\b/i.test(url);
      const isPdf = /\b(download|pdf|quote\s*summary|view\s*summary)\b/i.test(lower) || /\.pdf/i.test(url);
      if (isBind) {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="plr-bind-btn">` +
          `Bind Policy <span class="plr-bind-btn__icon">🔗</span></a>`;
      }
      if (isPdf) {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="plr-pdf-btn">` +
          `<span class="plr-pdf-btn__icon">📄</span> ${text}</a>`;
      }
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="plr-link">${text}</a>`;
    })
    // Bare URLs (not already inside href) → styled links / bind buttons
    .replace(/(?<!href=")(https?:\/\/[^\s<)"]+)/g, (url) => {
      const isBind = /\b(bind|offer|quote|apply|enroll|portal)\b/i.test(url);
      if (isBind) {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="plr-bind-btn">` +
          `Bind Policy <span class="plr-bind-btn__icon">🔗</span></a>`;
      }
      const label = url.length > 60 ? url.slice(0, 57) + '...' : url;
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="plr-link">${label}</a>`;
    })
    // Separators
    .replace(/^---$/gm, '')
    // Collapse whitespace
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\n/g, '<br />');
}

export default PLRChatView;
